import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Crown,
  Music2,
  Mic2,
  Piano,
  Brain,
  HeartHandshake,
  Sparkles,
  Star,
  ShieldCheck,
  Gift,
  CheckCircle2,
  ArrowRight,
  Quote,
} from "lucide-react";
import { GoldParticles } from "../components/effects/GoldParticles";
import { MaskLine, maskLineParent } from "../components/effects/MaskLine";
import { useLenis } from "../hooks/useLenis";
import {
  LOGO_URL,
  IMAGES,
  TEAM,
  FOUNDER_IMG,
  STUDENTS_IMG,
  VSL_VIDEO_URL,
} from "../data/content";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div
    variants={reveal}
    custom={i}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    className={className}
  >
    {children}
  </motion.div>
);

const CtaButton = ({ label, testId, size = "lg", variant = "primary" }) => {
  const base =
    "group inline-flex items-center justify-center gap-3 rounded-full font-body font-semibold tracking-wide transition-all duration-300";
  const sizes =
    size === "lg"
      ? "px-9 py-4 text-base md:text-lg"
      : "px-6 py-3 text-sm md:text-base";
  const variants =
    variant === "primary"
      ? "bg-[#D4AF37] text-[#0B0E17] hover:bg-[#e6c451] hover:-translate-y-0.5 cta-glow"
      : "border border-[#D4AF37]/60 text-[#FDFBF7] hover:bg-[#D4AF37]/10 hover:-translate-y-0.5";
  return (
    <Link
      to="/candidature"
      data-testid={testId}
      className={`${base} ${sizes} ${variants}`}
    >
      {label}
      <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
};

const SectionLabel = ({ children }) => (
  <span className="inline-block font-body text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
    {children}
  </span>
);

export default function Landing() {
  useLenis();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);

  const benefits = [
    {
      icon: Brain,
      title: "La Pyramide des apprentissages",
      text: "2 techniques puissantes pour mémoriser paroles et accords, même sans temps ni prédisposition.",
    },
    {
      icon: Mic2,
      title: "Une voix qui transporte",
      text: "Développez l'oreille musicale, le souffle et l'harmonisation pour devenir un chantre impactant.",
    },
    {
      icon: Piano,
      title: "Un jeu plus gospel",
      text: "Les clés d'un accompagnement fluide au piano et à la guitare qui fait vibrer votre assemblée.",
    },
    {
      icon: Sparkles,
      title: "5 leviers de progression",
      text: "Décuplez vos capacités d'apprentissage et progressez plus vite que vous ne l'imaginez.",
    },
    {
      icon: HeartHandshake,
      title: "Autonomie & service",
      text: "Notre technique secrète pour devenir autonome rapidement et servir votre communauté.",
    },
    {
      icon: Music2,
      title: "Adoration authentique",
      text: "Retrouvez enfin la satisfaction et la profondeur dans votre louange, sans frustration.",
    },
  ];

  const problems = [
    {
      title: "L'effort sans résultat",
      text: "Vous répétez, vous essayez… mais le son que vous désirez ne vient pas. La frustration s'installe.",
    },
    {
      title: "Des méthodes qui déçoivent",
      text: "Cours dispersés, tutoriels amateurs, promesses accélérées : le résultat n'est jamais au rendez-vous.",
    },
    {
      title: "Le manque de temps",
      text: "Emploi du temps chargé, vie de famille, service à l'église : difficile de progresser vraiment.",
    },
  ];

  const testimonials = [
    {
      name: "Élève de chant",
      img: "https://customer-assets.emergentagent.com/job_divine-journey-53/artifacts/97t72d3g_86601666_10157987947229110_2493959767869882368_o.jpeg",
      objPos: "object-[center_25%]",
      quote:
        "Depuis mon inscription j'ai beaucoup progressé au niveau du souffle grâce à des exercices respiratoires simples, que l'on peut facilement travailler à la maison.",
    },
    {
      name: "Élève de piano / guitare",
      img: "https://customer-assets.emergentagent.com/job_divine-journey-53/artifacts/bnmlst9r_WhatsApp%20Image%202020-03-26%20at%2017.42.58.jpeg",
      objPos: "object-[center_30%]",
      quote:
        "Avant, je ne m'intéressais pas aux instruments. La patience et la pédagogie des professeurs m'ont permis de jouer mes premiers morceaux. 5 mois plus tard, je suis pleinement satisfait.",
    },
    {
      name: "Élève de piano",
      img: "https://customer-assets.emergentagent.com/job_divine-journey-53/artifacts/3pkqd17v_IMG_20190816_203744.webp",
      objPos: "object-[center_20%]",
      quote:
        "Totalement débutante au départ, j'ai appris de vrais accords de gospel en 6 mois. La méthodologie est claire et concise, on ne joue pas comme un débutant.",
    },
  ];

  const bonuses = [
    {
      icon: Star,
      title: "Fiche de progression",
      text: "1 compétence à valider chaque mois durant 6 à 8 mois, envoyée après votre évaluation.",
    },
    {
      icon: Mic2,
      title: "RDV gratuit de 30 min",
      text: "Un échange avec un expert pour cerner vos besoins et vous présenter la méthode.",
    },
    {
      icon: HeartHandshake,
      title: "Communauté d'élèves",
      text: "Accès garanti à notre communauté de plus de 1000 élèves et anciens élèves.",
    },
  ];

  const press = [
    "Superprof",
    "Trace Gospel",
    "Chrétiens Lifestyle",
    "Trustpilot 4,5 / 5",
    "Théâtre Saint-Michel",
  ];

  return (
    <div className="relative bg-[#0B0E17] text-[#FDFBF7] overflow-hidden">
      {/* ================= NAV ================= */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div className="glass border-x-0 border-t-0">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-10">
            <div className="flex items-center gap-3" data-testid="nav-brand">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-10 object-contain md:h-12 md:w-12" />
              <div className="leading-tight">
                <p className="font-heading text-sm md:text-base text-[#FDFBF7]">
                  1<sup>er</sup> Conservatoire
                </p>
                <p className="font-body text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#C9A04E]">
                  de la Musique Chrétienne
                </p>
              </div>
            </div>
            <div className="hidden md:block">
              <CtaButton label="Candidater" testId="nav-cta-button" size="sm" />
            </div>
          </div>
        </div>
      </header>

      {/* ================= HERO / VSL ================= */}
      <section ref={heroRef} className="relative min-h-screen w-full overflow-hidden pt-28 pb-20">
        <motion.div
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 -z-10"
        >
          <img
            src={IMAGES.heroBg}
            alt="Cathédrale"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 -z-10 bg-[#0B0E17]/85" />
        <div className="absolute inset-0 -z-10 radial-glow" />
        <div className="light-rays -z-10" />
        <GoldParticles count={28} />

        <div className="relative mx-auto flex max-w-5xl flex-col items-center px-5 text-center">
          <Reveal i={0}>
            <img
              src={LOGO_URL}
              alt="1er Conservatoire de la Musique Chrétienne"
              className="mx-auto mb-8 h-28 w-28 object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.35)] md:h-36 md:w-36"
              data-testid="hero-logo"
            />
          </Reveal>

          <Reveal i={1}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#1A2033]/40 px-4 py-1.5 backdrop-blur">
              <Crown className="h-4 w-4 text-[#D4AF37]" />
              <span className="font-body text-xs uppercase tracking-[0.24em] text-[#C9A04E]">
                Depuis 2018 · France
              </span>
            </div>
          </Reveal>

          <motion.h1
            variants={maskLineParent}
            initial="hidden"
            animate="show"
            className="font-heading text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl"
            data-testid="hero-headline"
          >
            <MaskLine>
              Excellez enfin dans{" "}
              <span className="text-gold-gradient shimmer font-medium">la louange</span>
            </MaskLine>
            <MaskLine>et touchez les cœurs</MaskLine>
          </motion.h1>

          <Reveal i={3}>
            <p className="mx-auto mt-6 max-w-2xl font-body text-base font-light text-[#A1A9C3] md:text-lg">
              Découvrez la méthode qui permet à tout passionné de musique chrétienne
              de maîtriser sa voix ou son instrument — même avec un emploi du temps chargé.
            </p>
          </Reveal>

          {/* VSL PLAYER */}
          <Reveal i={4} className="mt-12 w-full">
            <div className="relative mx-auto max-w-xl">
              <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-[#D4AF37]/10 blur-2xl" />
              <div className="glass overflow-hidden rounded-3xl p-2 md:p-3">
                <div
                  className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black"
                  data-testid="vsl-player"
                >
                  <video
                    className="h-full w-full bg-black"
                    src={VSL_VIDEO_URL}
                    poster={IMAGES.choir}
                    controls
                    playsInline
                    preload="metadata"
                    data-testid="vsl-video"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal i={5} className="mt-10">
            <CtaButton
              label="Accéder au formulaire de candidature"
              testId="hero-cta-button"
            />
            <p className="mt-4 font-body text-xs uppercase tracking-[0.2em] text-[#A1A9C3]">
              Places limitées · Accompagnement personnalisé sur 6 à 8 mois
            </p>
          </Reveal>
        </div>
      </section>

      {/* ================= PRESS MARQUEE ================= */}
      <section className="border-y border-[#D4AF37]/10 bg-[#0B0E17] py-6">
        <div className="relative overflow-hidden">
          <div className="marquee gap-16 px-8">
            {[...press, ...press].map((p, idx) => (
              <span
                key={idx}
                className="flex shrink-0 items-center gap-3 font-heading text-lg text-[#FDFBF7]/40 md:text-xl"
              >
                <Star className="h-4 w-4 text-[#D4AF37]/60" />
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROBLEM / AGITATION ================= */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <div className="max-w-2xl">
              <SectionLabel>Le vrai problème</SectionLabel>
              <h2 className="font-heading text-3xl font-light leading-tight tracking-tight md:text-5xl">
                Vous rêvez d'une louange fluide et inspirante… mais il se passe souvent{" "}
                <span className="italic text-[#C9A04E]">ceci : rien.</span>
              </h2>
              <p className="mt-6 font-body text-[#A1A9C3] md:text-lg">
                Nous connaissons cette frustration — nous l'avons vécue. C'est pour cela que
                nous avons passé 10 ans à créer une méthode qui fonctionne vraiment.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3 md:gap-8">
            {problems.map((p, i) => (
              <Reveal key={p.title} i={i}>
                <div
                  className="glass h-full rounded-2xl p-8 transition-transform duration-500 hover:-translate-y-2"
                  data-testid={`problem-card-${i}`}
                >
                  <span className="font-heading text-5xl text-[#D4AF37]/40">
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 font-heading text-2xl">{p.title}</h3>
                  <p className="mt-3 font-body text-sm text-[#A1A9C3]">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BENEFITS / DISCOVERY ================= */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 radial-glow" />
        <div className="relative mx-auto max-w-6xl px-5">
          <Reveal>
            <div className="mx-auto max-w-3xl text-center">
              <SectionLabel>Ce que vous allez découvrir</SectionLabel>
              <h2 className="font-heading text-3xl font-light leading-tight tracking-tight md:text-5xl">
                Une méthode révolutionnaire :{" "}
                <span className="text-gold-gradient">« CMC-EdgarDale »</span>
              </h2>
              <p className="mt-6 font-body text-[#A1A9C3] md:text-lg">
                Pas de blabla théorique, pas de fausses promesses. Une mine de conseils
                puissants et concrets, applicables dès le premier jour.
              </p>
            </div>
          </Reveal>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} i={i % 3}>
                  <div
                    className="glass group h-full rounded-2xl p-8 transition-transform duration-500 hover:-translate-y-2"
                    data-testid={`benefit-card-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 text-[#D4AF37] transition-transform duration-500 group-hover:scale-110">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-2xl">{b.title}</h3>
                    <p className="mt-3 font-body text-sm text-[#A1A9C3]">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-14 text-center">
            <CtaButton label="Je veux progresser" testId="benefits-cta-button" />
          </Reveal>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <div className="text-center">
              <SectionLabel>En voici la preuve</SectionLabel>
              <h2 className="font-heading text-3xl font-light tracking-tight md:text-5xl">
                Nos élèves nous ont noté{" "}
                <span className="text-gold-gradient">4,5 / 5</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={i} i={i}>
                <figure
                  className="glass group h-full overflow-hidden rounded-2xl"
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={t.img}
                      alt={t.name}
                      className={`h-full w-full object-cover ${t.objPos || "object-center"} transition-transform duration-700 group-hover:scale-105`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2033] to-transparent" />
                    <Quote className="absolute bottom-4 right-4 h-10 w-10 text-[#D4AF37]/70" />
                  </div>
                  <div className="p-7">
                    <div className="mb-3 flex gap-1 text-[#D4AF37]">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                    <blockquote className="font-heading text-lg italic leading-relaxed text-[#FDFBF7]/90">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mt-4 font-body text-xs uppercase tracking-[0.2em] text-[#C9A04E]">
                      {t.name}
                    </figcaption>
                  </div>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOUNDER / TEAM ================= */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 -z-10 rounded-3xl bg-[#D4AF37]/10 blur-2xl" />
              <img
                src={STUDENTS_IMG}
                alt="Nos élèves en concert"
                className="h-[520px] w-full rounded-3xl object-cover object-center"
                data-testid="founder-image"
              />
            </div>
          </Reveal>
          <Reveal i={1}>
            <div>
              <SectionLabel>Qui sommes-nous ?</SectionLabel>
              <h2 className="font-heading text-3xl font-light leading-tight tracking-tight md:text-5xl">
                Des professeurs passionnés, une pédagogie éprouvée.
              </h2>
              <p className="mt-6 font-body text-[#A1A9C3] md:text-lg">
                Fondé par Jonathan Vouteau, professeur de musique issu du Conservatoire
                International de Paris, le 1<sup>er</sup> Conservatoire de la Musique
                Chrétienne réunit des enseignants d'excellence : coachs vocaux,
                professeurs de piano, guitare et saxophone.
              </p>
              <p className="mt-4 font-body text-[#A1A9C3] md:text-lg">
                Depuis 10 ans, nous cherchons les meilleures manières d'aider choristes
                et musiciens à progresser à l'église — sérieusement, avec de vrais
                professeurs expérimentés.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6 border-t border-[#D4AF37]/15 pt-8">
                {[
                  { n: "10 ans", l: "d'expertise" },
                  { n: "1000+", l: "élèves formés" },
                  { n: "6", l: "professeurs experts" },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-heading text-3xl text-gold-gradient md:text-4xl">
                      {s.n}
                    </p>
                    <p className="font-body text-xs uppercase tracking-[0.15em] text-[#A1A9C3]">
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= TEAM GRID ================= */}
      <section className="relative py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <div className="text-center">
              <SectionLabel>Notre équipe</SectionLabel>
              <h2 className="font-heading text-3xl font-light tracking-tight md:text-5xl">
                Des professeurs <span className="text-gold-gradient">d'exception</span>
              </h2>
              <p className="mx-auto mt-5 max-w-xl font-body text-[#A1A9C3] md:text-lg">
                Chaque enseignant est un musicien reconnu, dédié à faire grandir votre talent.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 md:gap-8">
            {TEAM.map((m, i) => (
              <Reveal key={i} i={i % 3}>
                <figure
                  className="glass group h-full overflow-hidden rounded-2xl"
                  data-testid={`team-card-${i}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={m.img}
                      alt={m.name || m.role}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E17] via-[#0B0E17]/10 to-transparent" />
                  </div>
                  <figcaption className="p-6 text-center">
                    {m.name && (
                      <p className="font-heading text-2xl text-gold-gradient">{m.name}</p>
                    )}
                    <h3 className="font-heading text-xl text-[#FDFBF7]">{m.role}</h3>
                    <p className="mt-1 font-body text-xs uppercase tracking-[0.18em] text-[#C9A04E]">
                      {m.detail}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= GUARANTEE ================= */}
      <section className="relative py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal>
            <div
              className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-[#1A2033]/50 p-10 text-center backdrop-blur-xl md:p-16"
              data-testid="guarantee-section"
            >
              <div className="light-rays opacity-40" />
              <div className="relative">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]">
                  <ShieldCheck className="h-8 w-8" />
                </div>
                <h2 className="mt-6 font-heading text-3xl font-light tracking-tight md:text-4xl">
                  Satisfait ou remboursé — 14 jours
                </h2>
                <p className="mx-auto mt-5 max-w-2xl font-body text-[#A1A9C3] md:text-lg">
                  Si vous estimez que la méthode « CMC-EdgarDale » ne constitue pas le
                  meilleur investissement de l'année pour votre progression, un simple
                  email suffit : vous êtes remboursé sous 14 jours. Le seul risque, c'est
                  de laisser passer cette opportunité.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= BONUS + FINAL CTA ================= */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E17] via-[#141a2b] to-[#0B0E17]" />
        <GoldParticles count={18} />
        <div className="relative mx-auto max-w-6xl px-5">
          <Reveal>
            <div className="text-center">
              <SectionLabel>Vos bonus offerts</SectionLabel>
              <h2 className="font-heading text-3xl font-light tracking-tight md:text-5xl">
                Ce que vous obtenez en{" "}
                <span className="text-gold-gradient">candidatant</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {bonuses.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} i={i}>
                  <div
                    className="glass h-full rounded-2xl p-8"
                    data-testid={`bonus-card-${i}`}
                  >
                    <Gift className="mb-4 h-6 w-6 text-[#D4AF37]" />
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 text-[#D4AF37]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-heading text-xl">{b.title}</h3>
                    <p className="mt-2 font-body text-sm text-[#A1A9C3]">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-16">
            <div className="glass mx-auto max-w-3xl rounded-3xl p-10 text-center md:p-14">
              <Crown className="mx-auto h-10 w-10 text-[#D4AF37]" />
              <h3 className="mt-5 font-heading text-3xl font-light tracking-tight md:text-4xl">
                Passez à l'action dès aujourd'hui
              </h3>
              <ul className="mx-auto mt-6 max-w-md space-y-3 text-left">
                {[
                  "Un RDV gratuit avec un expert",
                  "Un accompagnement personnalisé sur 6 à 8 mois",
                  "L'accès à notre communauté d'élèves",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 font-body text-[#FDFBF7]/90">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-[#D4AF37]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <CtaButton
                  label="Accéder au formulaire de candidature"
                  testId="final-cta-button"
                />
              </div>
              <p className="mt-4 font-body text-xs uppercase tracking-[0.2em] text-[#A1A9C3]">
                Ne laissez pas passer votre chance
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-[#D4AF37]/10 bg-[#0B0E17] py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-5 text-center">
          <img src={LOGO_URL} alt="Logo" className="h-16 w-16 object-contain" />
          <p className="font-heading text-lg text-[#FDFBF7]">
            1<sup>er</sup> Conservatoire de la Musique Chrétienne · France
          </p>
          <p className="max-w-md font-body text-xs text-[#A1A9C3]">
            Cours d'excellence de musique chrétienne et de chant gospel, dispensés par
            des professionnels.
          </p>
          <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#A1A9C3]/60">
            © {new Date().getFullYear()} — Tous droits réservés
          </p>
        </div>
      </footer>

      {/* ================= STICKY MOBILE CTA ================= */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center border-t border-[#D4AF37]/20 bg-[#0B0E17]/90 p-3 backdrop-blur-xl md:hidden">
        <CtaButton label="Candidater maintenant" testId="sticky-cta-button" size="sm" />
      </div>
    </div>
  );
}
